// ゲストの Google 連携：設定の「Googleと連携」と、トップの帯・勝利後のダイアログ（連携のおすすめ）で共用。
import { api, ApiError, track, type LinkFrom } from '../api';
import { refreshMe, state } from '../state';
import { renderGoogleButton } from './gsi';
import { confirmDialog, openModal, toast } from './overlay';
import { errorText } from './status';

/**
 * ゲストのアカウントに Google を結び付ける。その Google に別のデータがあるときは、
 * 確認してからそちらへ切り替える（今のゲストのデータには戻れない）。
 * retry：一度きりの値（nonce）を取り直してボタンを出し直す。done：連携できた。from：連携をすすめた場面（運営の集計用）
 */
export async function linkGoogle(credential: string, retry: () => void, done: (() => void) | undefined, from: LinkFrom) {
  try {
    await api.linkGoogle(credential, from);
    await refreshMe();
    toast('Googleと連携しました');
    done ? done() : retry();
  } catch (e) {
    if (!(e instanceof ApiError)) return void toast(errorText(e), 'error', 4000);
    if (e.code === 'invalid_token') {
      toast('Googleの確認に失敗しました。もう一度お試しください。', 'error', 4000);
      return retry();
    }
    if (e.code !== 'google_in_use') return void toast(errorText(e), 'error', 4000);
    const ok = await confirmDialog('このGoogleアカウントには、すでに別のデータがあります。\nそちらに切り替えますか？切り替えると、今のゲストのデータには戻れません。', {
      ok: '切り替える',
      danger: true,
      title: 'Google連携',
    });
    if (!ok) return;
    try {
      await api.googleLogin(credential);
      location.reload(); // アカウントが変わるので画面の状態を作り直す
    } catch (err) {
      toast(errorText(err), 'error', 4000);
    }
  }
}

/** slot に連携のボタンを出す（失敗・未設定なら note に書く）。ゲストにボタンを見せたことを from の場面として送る */
export function mountLinkButton(slot: HTMLElement, note: HTMLElement | null, done: (() => void) | undefined, from: LinkFrom) {
  if (state.me?.user.guest) track(`link.${from}`);
  const show = () =>
    void renderGoogleButton(slot, (c) => void linkGoogle(c, show, done, from), 'continue_with').then((r) => {
      if (!note) return;
      if (r === 'unconfigured') note.textContent = 'Google連携は準備中です';
      else if (r === 'failed') note.textContent = 'Googleのボタンを読み込めませんでした。通信状況を確認して再読み込みしてください。';
    });
  show();
}

/** 連携のダイアログ（トップの帯・勝利後・ゲストに使えない機能） */
export function openLinkDialog(from: LinkFrom, lead = 'ゲストのデータは、この端末のブラウザにだけ保存されています。') {
  if (!state.me?.user.guest) return;
  const m = openModal(
    `<div class="link-dialog">
      <p>${lead}</p>
      <p class="small">Googleと連携すると、機種変更やほかの端末でも同じデータで遊べます。</p>
      <div class="gsi-slot" aria-label="Googleと連携"></div><p class="gsi-note muted small"></p>
      <div class="btn-row"><button class="btn" type="button" data-close>あとで</button></div>
    </div>`,
    { title: 'Google連携' },
  );
  mountLinkButton(m.body.querySelector('.gsi-slot')!, m.body.querySelector('.gsi-note'), () => m.close(), from);
}

// ---------- おすすめを出す頻度（端末ごと） ----------
const KEY = 'hk.linkNudge';
type Nudge = { topHiddenDay?: string; winDay?: string; wins?: number };
const read = (): Nudge => {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Nudge;
  } catch {
    return {};
  }
};
const write = (n: Nudge) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(n));
  } catch {
    // 保存できなくても毎回出るだけ
  }
};
const today = () => new Date().toLocaleDateString('sv');

/** トップの帯を出すか（×で閉じたらその日だけ出さない。日付が変わればまた出す） */
export const showTopNudge = () => !!state.me?.user.guest && read().topHiddenDay !== today();
export const hideTopNudge = () => write({ ...read(), topHiddenDay: today() });

/** ゲストには使えない機能（オート・チャットの発言）の鍵の印 */
export const guestLockHtml = (lockIcon: string) => (state.me?.user.guest ? `<em class="guest-lock" aria-label="Google連携で使えます">${lockIcon}</em>` : '');
/** ゲストなら連携をすすめて true（呼び出し側はそこで止める） */
/** ゲストが2章以降へ出撃しようとしたときの案内 */
export const GUEST_CHAPTER_MSG = 'ゲストで遊べるのは第1章クリアまでです。続きはGoogle連携後にお楽しみください。';

export function guestBlocked(lead: string, from: LinkFrom): boolean {
  if (!state.me?.user.guest) return false;
  openLinkDialog(from, lead);
  return true;
}

/** 勝利後に連携をすすめるか：最初の勝利と、そのあとは1日1回まで */
export function takeWinNudge(): boolean {
  if (!state.me?.user.guest) return false;
  const n = read();
  const wins = (n.wins ?? 0) + 1;
  const ok = wins === 1 || n.winDay !== today();
  write({ ...n, wins, ...(ok ? { winDay: today() } : {}) });
  return ok;
}
